import { Navbar, SearchInput } from '@/components';
import { analysisStatusColor } from '@/constants';
import { PageSettingsContext } from '@/contexts';
import {
  AnalysisBatchClientType,
  AnalysisStatus,
  DAYJS_FORMAT,
  ENTITY,
  UserActions,
} from '@/enums';
import {
  useGetSubdomainQuery,
  useParseQueryString,
  useQueryNavigate,
  useRowSelectionTable,
  useToggleModal,
} from '@/hooks';
import useCheckScopeCombinations from '@/hooks/useCheckScopeCombinations';
import { AnalysisEntity, AnalysisParams, MetaTag } from '@/interfaces';
import { useGetCommercialAnalysisBatches } from '@/queries';
import { PRIVATE_ROUTES } from '@/routes';
import { AnalysisSampleKitEntity } from '@/types';
import { formatAnalysisId, rowKey, translatedTextFunc } from '@/utils';
import {
  CustomBadge,
  CustomButton,
  CustomTable,
  CustomTag,
  DuplicateAmiliPagination,
  Icon,
  PAGE_DEFAULT,
} from '@amili-v3/amili-v3-design';
import { Popover, Tooltip } from 'antd';
import { ColumnType } from 'antd/es/table';
import dayjs from 'dayjs';
import { FC, memo, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useLocation, useNavigate } from 'react-router-dom';
import { getScrollY } from '../../Order/utils';
import {
  BoldTag,
  NoWrapSpan,
  PageLabel,
  SharedContentWrapper,
  SharedPageWrapper,
} from '../../styles';
import AnalysisTabs from '../home/AnalysisTabs';
import AnalysisDropdown from '../research-analysis/AnalysisDropdown';
import AnalysisFilter from '../research-analysis/AnalysisFilter';
import { MLErrorWrapper } from '../research-analysis/style';
import { GeneralAnalysisProps } from '../utils';
import CreateCommercialAnalysisModal from './sections/CreateCommercialAnalysisModal';

type CommercialManagementProps = GeneralAnalysisProps & {};
const CommercialAnalysisManagement: FC<CommercialManagementProps> = props => {
  const { activeTab } = props;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const handleNavigate = useQueryNavigate<AnalysisParams>();
  const qsParams = useParseQueryString<AnalysisParams>();
  const { pathname } = useLocation();

  const { subdomain } = useGetSubdomainQuery();
  const isAmiliOrg = subdomain === 'amili';

  const { createCommercialModal } = useToggleModal(['createCommercial']);

  const { pageSettings: originPageSettings } = useContext(PageSettingsContext);
  const analysisParams = useMemo<AnalysisParams>(() => {
    const { status, take, ...remainingParams } = qsParams;
    const pageSize = originPageSettings?.[pathname];

    return {
      ...remainingParams,
      take: pageSize ?? take,
      status: Object.values(AnalysisStatus).includes(status as AnalysisStatus)
        ? status
        : undefined,
    };
  }, [originPageSettings, pathname, qsParams]);
  const {
    data: commercialAnalysisData,
    isFetching: isFetchingCommercialAnalysis,
    isLoading: isLoadingCommercialAnalysis,
    pageSetting,
  } = useGetCommercialAnalysisBatches({
    params: analysisParams,
  });
  const total = commercialAnalysisData?.itemCount ?? 0;
  const commercialAnalysisBatches = commercialAnalysisData?.data ?? [];

  const {
    selectedCount,
    columnTitle,
    onChangeRowSelection,
    checkedListArray,
    setCheckedListOfAllPage,
    setAllPageRecords,
    setRecords,
  } = useRowSelectionTable({
    page: qsParams?.page ?? PAGE_DEFAULT,
    totalPageSize: total,
    dataSource: commercialAnalysisBatches,
    rowKey,
  });

  const columns: ColumnType<AnalysisEntity>[] = [
    {
      title: t('analysis.analysisBatchID'),
      dataIndex: 'displayId',
      width: 200,
      render: (val: string, record) => {
        const fullDetailUrl =
          PRIVATE_ROUTES.ANALYSIS.ROOT +
          '/' +
          PRIVATE_ROUTES.ANALYSIS.COMMERCIAL_DETAIL;

        return !record?.deletedAt ? (
          <BoldTag
            onClick={() => {
              navigate(generatePath(fullDetailUrl, { displayId: val }));
            }}
          >
            <b>{val}</b>
          </BoldTag>
        ) : (
          <b>{val}</b>
        );
      },
    },
    {
      title: t('project.projectName'),
      dataIndex: ['project', 'name'],
      width: 200,
      render: (val: string) => val,
    },
    {
      title: t('common.dateCreated'),
      dataIndex: 'dateOfCreation',
      width: 150,
      render: (val: string) =>
        dayjs(val).format(DAYJS_FORMAT.FULL_DATE_FORMAT_2),
    },
    {
      title: t('common.numberOfSamples'),
      dataIndex: 'amountOfSamples',
      width: 150,
      render: (val: number) => val,
      align: 'center',
    },
    {
      title: t('analysis.sendForAnalysis'),
      dataIndex: 'dateOfSendAnalysis',
      width: 200,
      render: (val: string) =>
        val ? dayjs(val).format(DAYJS_FORMAT.FULL_DATE_FORMAT_2) : '--',
    },
    {
      title: t('analysis.analysisStatus'),
      dataIndex: 'status',
      width: 150,
      render: (status: AnalysisStatus, record) => {
        const { amountOfAnalyzingSamples = 0, amountOfSamples = 0 } = record;
        const isValidAmount =
          Number(amountOfSamples) && Number(amountOfAnalyzingSamples);
        const customStatus =
          status === AnalysisStatus.TO_BE_ANALYSED && isValidAmount
            ? `${Number(amountOfAnalyzingSamples)}/${Number(
                amountOfSamples,
              )} Analysing`
            : t(`analysis.status.${status}`);
        const sampleWithErrorAnalysis = record.analysisSampleKits?.reduce(
          (acc, cur) => {
            const sample = cur.metaData ? cur.metaData[0] : ({} as MetaTag);
            if (!!sample?.value) {
              acc.push(cur);
            }
            return acc;
          },
          [] as AnalysisSampleKitEntity[],
        );
        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Popover
              color="#FFF3F1"
              content={
                sampleWithErrorAnalysis?.length! > 0 && isAmiliOrg ? (
                  <div
                    style={{
                      overflow: 'auto',
                      maxHeight: '400px',
                    }}
                  >
                    <strong>{t('analysis.reasonForMLError')}</strong>
                    {sampleWithErrorAnalysis?.map(
                      (sample: AnalysisSampleKitEntity) => (
                        <MLErrorWrapper key={sample.analysisId}>
                          <>
                            <strong>
                              {`${formatAnalysisId(
                                sample?.analysisId?.toUpperCase(),
                              )}: `}
                            </strong>
                            {translatedTextFunc(
                              sample?.metaData?.[0]?.value ?? '',
                              'analysis.mlError',
                            )}
                          </>
                        </MLErrorWrapper>
                      ),
                    )}
                  </div>
                ) : null
              }
            >
              <CustomTag {...analysisStatusColor[status]}>
                {customStatus}
              </CustomTag>
            </Popover>
            {Number(record.amountOfUndersupportSamples || 0) ? (
              <Tooltip
                title={t('analysis.tooltipMessageBatchHasUnderSupportSample')}
                trigger={'hover'}
                placement="bottom"
              >
                <span
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    background: 'var(--error)',
                  }}
                />
              </Tooltip>
            ) : null}
          </div>
        );
      },
    },
    {
      className: 'actions',
      fixed: 'right',
      render: (_, record: AnalysisEntity) => (
        <AnalysisDropdown
          key={record.id}
          analysisRecord={record}
          batchClientType={AnalysisBatchClientType.COMMERCIAL}
        />
      ),
      width: 68,
    },
  ];

  const { canCreateCommercialAnalysisBatch: canCreate } =
    useCheckScopeCombinations([
      [
        {
          entity: ENTITY.COMMERCIAL_ANALYSIS_BATCH,
          action: UserActions.CREATE,
        },
      ],
    ]);
  const canSearch = true;
  const canFilter = true;

  const headerSectionsLeftSide = [
    <PageLabel
      key="title"
      onClick={() => navigate(PRIVATE_ROUTES.ANALYSIS.ROOT)}
    >
      {t('titles.analysis')}
    </PageLabel>,
    canCreate && (
      <CustomButton
        key="add-new"
        type="white"
        prefix={<Icon name="addCircle" />}
        onClick={createCommercialModal.toggle}
      >
        {t('analysis.addNewCommercialAnalysis')}
      </CustomButton>
    ),
  ];
  const bulkActionsLeft = [
    headerSectionsLeftSide[0],
    <CustomBadge
      key="selected"
      color="primary"
      suffix={<Icon name="close" />}
      onClick={() => {
        setCheckedListOfAllPage({});
        setAllPageRecords([]);
        setRecords([]);
      }}
    >
      <NoWrapSpan>
        {selectedCount} {t('common.selected')}
      </NoWrapSpan>
    </CustomBadge>,
  ];
  const headerSectionsRightSide = [
    canSearch && <SearchInput key="search" />,
    canFilter && <AnalysisFilter key="filter" />,
  ];

  return (
    <SharedPageWrapper>
      <Navbar
        left={selectedCount > 0 ? bulkActionsLeft : headerSectionsLeftSide}
        right={selectedCount > 0 ? undefined : headerSectionsRightSide}
      />
      <AnalysisTabs activeKey={activeTab} />

      <SharedContentWrapper>
        <CustomTable
          columns={columns}
          rowKey={rowKey}
          rowSelection={{
            columnTitle,
            selectedRowKeys: checkedListArray,
            onChange: onChangeRowSelection,
            fixed: true,
          }}
          dataSource={commercialAnalysisBatches}
          scroll={{
            x: 1000,
            y: getScrollY({ hasTabs: true }),
          }}
          className="fixed-column"
          loading={isLoadingCommercialAnalysis || isFetchingCommercialAnalysis}
        />
        <DuplicateAmiliPagination
          total={total}
          pageSettingStates={pageSetting}
          onNavigate={handleNavigate}
        />
      </SharedContentWrapper>

      <CreateCommercialAnalysisModal
        isOpen={createCommercialModal.visible}
        toggleModal={createCommercialModal.toggle}
      />
    </SharedPageWrapper>
  );
};

export default memo(CommercialAnalysisManagement);
